
import React, { useState } from 'react';
import { Search, Plus, Filter } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

interface EnhancedSearchCreateToolbarProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onCreateClick: () => void;
  canCreateProfile: boolean;
  currentCount: number;
  maxProfiles: number;
  statusFilter?: string;
  onStatusFilterChange?: (value: string) => void;
}

export const EnhancedSearchCreateToolbar = ({
  searchQuery,
  onSearchChange,
  onCreateClick,
  canCreateProfile,
  currentCount,
  maxProfiles,
  statusFilter = 'all',
  onStatusFilterChange
}: EnhancedSearchCreateToolbarProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  
  const buttonText = isMobile 
    ? t('create_profile')
    : t('create_profile_with_count', { currentCount, maxProfiles });
  
  return (
    <div className="bg-gradient-to-r from-background to-muted/20 border border-border/50 rounded-xl p-3 md:p-4 shadow-sm">
      <div className={`${isMobile ? 'space-y-3' : 'flex flex-col md:flex-row gap-3 md:gap-4 md:items-center'}`}>
        {/* Search Input */}
        <div className="relative flex-1" data-tour="search">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={isMobile ? "Search profiles..." : t('search_ctrader_placeholder')}
            className={`pl-10 bg-background/50 border-border/60 focus:border-primary/50 focus:ring-2 focus:ring-primary/20 transition-all ${isMobile ? 'h-10' : ''}`}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter and Create Button Row */}
        <div className={`flex ${isMobile ? 'gap-2' : 'gap-3'} items-center ${isMobile ? 'justify-between' : ''}`}>
          {/* Status Filter */}
          {onStatusFilterChange && (
            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className={`${isMobile ? 'w-[120px] h-10' : 'w-[140px]'} bg-background/50 border-border/60`}>
                <Filter className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-2`} />
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="connected">Connected</SelectItem>
                <SelectItem value="disconnected">Disconnected</SelectItem>
              </SelectContent>
            </Select>
          )}

          {/* Create Button */}
          <Button 
            onClick={onCreateClick}
            disabled={!canCreateProfile}
            size={isMobile ? "sm" : "default"}
            className={`group bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] ${isMobile ? 'flex-1 h-10' : 'whitespace-nowrap'}`}
            data-tour="create-profile"
          >
            <Plus className={`${isMobile ? 'mr-1 h-3 w-3' : 'mr-2 h-4 w-4'} transition-transform duration-200 group-hover:rotate-90`} />
            <span className={`font-medium ${isMobile ? 'text-sm' : ''}`}>
              {isMobile ? 'Create' : buttonText}
            </span>
            {isMobile && (
              <span className="ml-1 text-xs opacity-75">
                ({currentCount}/{maxProfiles})
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
